export type GraphQLErrorLike = {
  message?: string;
  extensions?: {
    code?: string;
  };
  path?: unknown;
};

export type NetworkErrorWithResult = Error & {
  result?: {
    errors?: GraphQLErrorLike[];
  };
  statusCode?: number;
};

export const SESSION_VALIDATION_ERROR_MESSAGE =
  'We could not validate your SportsFi session. Sign out and sign in again.';

export const NETWORK_RECOVERY_ERROR_MESSAGE =
  'We could not reach SportsFi. Your session or connection may need to be refreshed.';

const authenticationErrorCodes = new Set([
  'JWT_INVALID',
  'JWT_UNABLE_TO_DECODE',
  'NOT_AUTHENTICATED',
  'UNAUTHENTICATED',
]);

const identityProviderUnavailableErrorCodes = new Set(['IDENTITY_PROVIDER_UNAVAILABLE', 'KEYCLOAK_GROUP_NOT_FOUND']);

const authenticationErrorMessages = ['Invalid JWT token', 'Unable to decode JWT token', 'Not authenticated'];

const identityProviderUnavailableErrorMessages = [
  'Identity provider is unavailable',
  'Identity provider unavailable',
  'Keycloak unavailable',
  'Keycloak group not found',
  'Account access group not found',
  'Keycloak',
];

const rawNetworkErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
  'NetworkError when attempting to fetch resource',
];

function normaliseErrorMessage(message?: string | null): string | undefined {
  const trimmedMessage = message?.trim();

  return trimmedMessage == null || trimmedMessage.length === 0 ? undefined : trimmedMessage;
}

export const isAuthenticationGraphQLError = (error: GraphQLErrorLike): boolean => {
  const message = normaliseErrorMessage(error.message);
  const code = error.extensions?.code;

  return (
    (code != null && authenticationErrorCodes.has(code)) ||
    (message != null && authenticationErrorMessages.some((rawMessage) => message.includes(rawMessage)))
  );
};

export const isIdentityProviderUnavailableGraphQLError = (error: GraphQLErrorLike): boolean => {
  const message = normaliseErrorMessage(error.message);
  const code = error.extensions?.code;

  return (
    (code != null && identityProviderUnavailableErrorCodes.has(code)) ||
    (message != null && identityProviderUnavailableErrorMessages.some((rawMessage) => message.includes(rawMessage)))
  );
};

export const getNetworkGraphQLErrors = (networkError: unknown): GraphQLErrorLike[] => {
  const result = (networkError as NetworkErrorWithResult | null)?.result;

  return Array.isArray(result?.errors) ? result.errors : [];
};

export const isAuthenticationNetworkError = (networkError: unknown): boolean => {
  const serverError = networkError as NetworkErrorWithResult | null;

  if (serverError?.statusCode === 401) {
    return true;
  }

  return getNetworkGraphQLErrors(networkError).some(isAuthenticationGraphQLError);
};

export const isIdentityProviderUnavailableNetworkError = (networkError: unknown): boolean =>
  getNetworkGraphQLErrors(networkError).some(isIdentityProviderUnavailableGraphQLError);

export const isApolloAuthenticationError = (error: unknown): boolean => {
  const apolloError = error as {
    graphQLErrors?: GraphQLErrorLike[];
    message?: string;
    networkError?: unknown;
  } | null;

  return (
    isAuthenticationGraphQLError({ message: apolloError?.message }) ||
    apolloError?.graphQLErrors?.some(isAuthenticationGraphQLError) === true ||
    isAuthenticationNetworkError(apolloError?.networkError)
  );
};

export const isApolloIdentityProviderUnavailableError = (error: unknown): boolean => {
  const apolloError = error as {
    graphQLErrors?: GraphQLErrorLike[];
    message?: string;
    networkError?: unknown;
  } | null;

  return (
    isIdentityProviderUnavailableGraphQLError({ message: apolloError?.message }) ||
    apolloError?.graphQLErrors?.some(isIdentityProviderUnavailableGraphQLError) === true ||
    isIdentityProviderUnavailableNetworkError(apolloError?.networkError)
  );
};

export const getSafeErrorMessage = (errorMessage?: string | null): string | undefined => {
  const message = normaliseErrorMessage(errorMessage);

  if (message == null) {
    return undefined;
  }

  if (
    authenticationErrorMessages.some((rawMessage) => message.includes(rawMessage)) ||
    identityProviderUnavailableErrorMessages.some((rawMessage) => message.includes(rawMessage))
  ) {
    return SESSION_VALIDATION_ERROR_MESSAGE;
  }

  if (rawNetworkErrorMessages.some((rawMessage) => message.includes(rawMessage))) {
    return NETWORK_RECOVERY_ERROR_MESSAGE;
  }

  return message;
};

export const getSafeApolloErrorMessage = (error: unknown): string | undefined => {
  if (error == null) {
    return undefined;
  }

  if (isApolloAuthenticationError(error) || isApolloIdentityProviderUnavailableError(error)) {
    return SESSION_VALIDATION_ERROR_MESSAGE;
  }

  const apolloError = error as {
    graphQLErrors?: GraphQLErrorLike[];
    message?: string;
    networkError?: unknown;
  } | null;

  const gqlError = apolloError?.graphQLErrors?.[0];
  const gqlMessage = getSafeErrorMessage(gqlError?.message);

  if (gqlMessage != null) {
    return gqlMessage;
  }

  const networkGraphQLError = getNetworkGraphQLErrors(apolloError?.networkError)[0];
  const networkGraphQLErrorMessage = getSafeErrorMessage(networkGraphQLError?.message);

  if (networkGraphQLErrorMessage != null) {
    return networkGraphQLErrorMessage;
  }

  const networkMessage = getSafeErrorMessage((apolloError?.networkError as { message?: string } | null)?.message);

  if (networkMessage != null) {
    return networkMessage;
  }

  return getSafeErrorMessage(apolloError?.message);
};
